import { AlarmClock, CircleCheck, CircleDashed, TriangleAlert } from 'lucide-react';
import { Fragment, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { progressOf, type BuildingView, type GroupStat, type ScheduleState } from '@pz/core';
import { h, num } from '../../lib/format';
import { useTip } from '../ui';
import { hoursFill, hoursInk, pctOr, progFill, progInk } from './common';

export type BuildingMode = 'progress' | 'schedule' | 'hours';

const SCHEDULE_FILL: Record<ScheduleState, string> = { ok: 'var(--good-soft)', risk: 'var(--warn-soft)', late: 'var(--bad-soft)', none: 'var(--sheet-2)' };
const SCHEDULE_INK: Record<ScheduleState, string> = { ok: 'var(--good)', risk: 'var(--warn)', late: 'var(--bad)', none: 'var(--graphite)' };
const SCHEDULE_ICON = { ok: CircleCheck, risk: TriangleAlert, late: AlarmClock, none: CircleDashed };

interface Geometry {
  bw: number;
  fh: number;
  roofH: number;
  gap: number;
  pad: number;
  titleH: number;
  /** depth of the oblique view: offset of the back edge to the right (dx) and up (dy) */
  dx: number;
  dy: number;
}

const FULL: Geometry = { bw: 268, fh: 38, roofH: 32, gap: 64, pad: 16, titleH: 44, dx: 22, dy: 12 };
const SMALL: Geometry = { bw: 58, fh: 9, roofH: 8, gap: 14, pad: 2, titleH: 0, dx: 6, dy: 3 };

/**
 * Elevation of the buildings of a project in an oblique view (front, side and top face; request 22 of round 4), drawn
 * from the floors of the tasks: one storey per floor, basements below the ground plane, the roof on top, building-wide
 * tasks ("Allgemein") on a sign below. Each storey is coloured by progress (green ramp, darker = further), by schedule
 * state (status colours with icon) or by hours (blue ramp), with the figures written on the front. The side and the
 * top are the same colour, shaded, so the colour keeps its meaning. Click or Enter on a storey filters the analysis.
 */
export function BuildingSvg({
  view,
  progressBy,
  mode = 'progress',
  selected,
  onSelect,
  small = false,
  maxWidth,
}: {
  view: BuildingView;
  progressBy: 'count' | 'plan';
  mode?: BuildingMode;
  selected?: { building: string; floor: string | null } | null;
  onSelect?: (building: string, floor: string | null) => void;
  small?: boolean;
  maxWidth?: number;
}) {
  const { t } = useTranslation();
  const tip = useTip();
  const g = small ? SMALL : FULL;
  const buildings = view.buildings;
  const areas = small ? [] : view.areas;
  const maxHours = Math.max(1, ...buildings.flatMap((b) => b.floors.map((f) => f.actualMinutes)), ...areas.map((a) => a.actualMinutes));
  const height = (rank: number) => (rank >= 1000 ? g.roofH : g.fh);
  const above = buildings.map((b) => b.floors.filter((f) => f.rank >= 0).reduce((s, f) => s + height(f.rank), 0));
  const below = buildings.map((b) => b.floors.filter((f) => f.rank < 0).length * g.fh);
  const maxAbove = Math.max(g.fh, ...above);
  const maxBelow = Math.max(0, ...below);
  const generalH = small ? 0 : buildings.some((b) => b.general) ? 46 : 0;
  const ground = g.pad + g.titleH + g.dy + maxAbove;
  const areaW = 150;
  const step = g.bw + g.dx + g.gap;
  const width = g.pad * 2 + buildings.length * step - (buildings.length ? g.gap : 0) + (areas.length ? g.gap + areas.length * (areaW + g.dx + 16) : 0);
  const totalH = ground + maxBelow + (maxBelow ? 10 : 6) + generalH + g.pad;

  const fillOf = (s: GroupStat) => (mode === 'schedule' ? SCHEDULE_FILL[s.schedule] : mode === 'hours' ? hoursFill(s.actualMinutes / maxHours) : progFill(progressOf(s, progressBy)));
  const inkOf = (s: GroupStat) => (mode === 'schedule' ? SCHEDULE_INK[s.schedule] : mode === 'hours' ? hoursInk(s.actualMinutes / maxHours) : progInk(progressOf(s, progressBy)));
  const valueText = (s: GroupStat) => (mode === 'hours' ? `${h(s.actualMinutes, 0)} h` : pctOr(progressOf(s, progressBy)));
  const describe = (place: string, s: GroupStat) =>
    `${place}: ${t('analysis.floorAria', { progress: pctOr(progressOf(s, progressBy)), done: s.done, tasks: s.tasks, overdue: s.overdue, hours: h(s.actualMinutes, 1) })}`;
  const tipContent = (place: string, s: GroupStat): ReactNode => (
    <div className="flex flex-col gap-0.5">
      <div className="font-semibold">{place}</div>
      <div className="num">
        {t('analysis.tipProgress', { progress: pctOr(progressOf(s, progressBy)), done: num(s.done), tasks: num(s.tasks) })}
      </div>
      <div className="num text-graphite">{t('analysis.tipStages', { progress: s.progress, review: s.review, open: s.open })}</div>
      {(s.overdue > 0 || s.dueSoon > 0) && <div className="num text-bad">{t('analysis.tipDue', { overdue: s.overdue, soon: s.dueSoon })}</div>}
      <div className="num text-graphite">{t('analysis.tipHours', { actual: h(s.actualMinutes, 1), plan: h(s.plannedMinutes, 0) })}</div>
      <div className="text-graphite">{t(`analysis.schedule.${s.schedule}`)}</div>
    </div>
  );
  const keyHandler = (fn: () => void) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fn();
    }
  };

  /**
   * One storey: front face with the figures, the side face to the right (shaded darker) and, for the storey at the top
   * (`top`), the top face (shaded lighter). A roof is a prism: trapezoid in front, sloped side.
   */
  const floorBox = (
    s: GroupStat,
    x: number,
    y: number,
    w: number,
    hgt: number,
    label: string,
    place: string,
    onClick: (() => void) | undefined,
    isSel: boolean,
    kind: 'floor' | 'roof' | 'ground' | 'area',
    top: boolean,
  ) => {
    const fill = s.tasks ? fillOf(s) : 'url(#pz-hatch)';
    const ink = s.tasks ? inkOf(s) : 'var(--graphite)';
    const Icon = SCHEDULE_ICON[s.schedule];
    const interactive = !!onClick && !small;
    const stroke = isSel ? 'var(--accent)' : 'var(--line-strong)';
    const sw = isSel ? 2.5 : small ? 0.75 : 1;
    const hh = hgt - (small ? 1 : 2);
    const { dx, dy } = g;
    const inset = small ? 8 : 34;
    const complete = s.tasks > 0 && s.done === s.tasks && mode === 'progress';
    return (
      <g
        role={interactive ? 'button' : 'img'}
        tabIndex={interactive ? 0 : undefined}
        aria-label={describe(place, s)}
        aria-pressed={interactive ? isSel : undefined}
        className={interactive ? 'cursor-pointer outline-none [&:focus-visible_.pz-frame]:stroke-[var(--focus)]' : undefined}
        onClick={interactive ? onClick : undefined}
        onKeyDown={interactive ? keyHandler(onClick!) : undefined}
        {...(small ? {} : tip.bind(tipContent(place, s)))}
      >
        {small && <title>{describe(place, s)}</title>}
        {kind === 'roof' ? (
          <>
            {/* sloped side of the roof, then the front */}
            <polygon points={`${x + w - 4},${y + hh} ${x + w - 4 + dx},${y + hh - dy} ${x + w - inset + dx},${y + 2 - dy} ${x + w - inset},${y + 2}`} fill={fill} stroke={stroke} strokeWidth={sw} />
            <polygon points={`${x + w - 4},${y + hh} ${x + w - 4 + dx},${y + hh - dy} ${x + w - inset + dx},${y + 2 - dy} ${x + w - inset},${y + 2}`} fill="#000" fillOpacity={0.18} aria-hidden />
            <polygon points={`${x + inset},${y + 2} ${x + inset + dx},${y + 2 - dy} ${x + w - inset + dx},${y + 2 - dy} ${x + w - inset},${y + 2}`} fill={fill} stroke={stroke} strokeWidth={sw} />
            <polygon points={`${x + inset},${y + 2} ${x + inset + dx},${y + 2 - dy} ${x + w - inset + dx},${y + 2 - dy} ${x + w - inset},${y + 2}`} fill="#fff" fillOpacity={0.28} aria-hidden />
            <polygon className="pz-frame" points={`${x + 4},${y + hh} ${x + w - 4},${y + hh} ${x + w - inset},${y + 2} ${x + inset},${y + 2}`} fill={fill} stroke={stroke} strokeWidth={sw} />
          </>
        ) : (
          <>
            {/* side face (right), top face (only the uppermost storey), front face */}
            <polygon points={`${x + w},${y} ${x + w + dx},${y - dy} ${x + w + dx},${y + hh - dy} ${x + w},${y + hh}`} fill={fill} stroke={stroke} strokeWidth={sw} />
            <polygon points={`${x + w},${y} ${x + w + dx},${y - dy} ${x + w + dx},${y + hh - dy} ${x + w},${y + hh}`} fill="#000" fillOpacity={0.2} aria-hidden />
            {top && (
              <>
                <polygon points={`${x},${y} ${x + dx},${y - dy} ${x + w + dx},${y - dy} ${x + w},${y}`} fill={fill} stroke={stroke} strokeWidth={sw} />
                <polygon points={`${x},${y} ${x + dx},${y - dy} ${x + w + dx},${y - dy} ${x + w},${y}`} fill="#fff" fillOpacity={0.3} aria-hidden />
              </>
            )}
            <rect className="pz-frame" x={x} y={y} width={w} height={hh} rx={small ? 0.5 : 1} fill={fill} stroke={stroke} strokeWidth={sw} />
          </>
        )}
        {!small && kind !== 'roof' && kind !== 'area' && (
          <g aria-hidden opacity={0.22} stroke={ink} fill="none" strokeWidth={1}>
            {Array.from({ length: 4 }, (_, i) => (
              <rect key={i} x={x + 92 + i * 22} y={y + 9} width={11} height={hgt - 20} rx={1} />
            ))}
          </g>
        )}
        {!small && kind === 'ground' && <rect aria-hidden x={x + w / 2 - 9} y={y + hgt - 24} width={18} height={22} rx={1} fill="none" stroke={ink} strokeOpacity={0.35} />}
        {!small && (
          <>
            <text x={x + 9} y={y + (kind === 'roof' ? hgt / 2 + 4 : 15)} fontSize={12} fontWeight={600} fill={ink} className="select-none">
              {label}
            </text>
            {kind !== 'roof' && (
              <text x={x + 9} y={y + 29} fontSize={10.5} fill={ink} className="num select-none" opacity={0.9}>
                {s.tasks ? `${num(s.done)}/${num(s.tasks)}` : t('analysis.noTasks')}
              </text>
            )}
            <text x={x + w - 9} y={y + (kind === 'roof' ? hgt / 2 + 5 : hgt / 2 + 4)} fontSize={kind === 'roof' ? 12 : 14} fontWeight={700} textAnchor="end" fill={ink} className="num select-none">
              {s.tasks ? valueText(s) : ''}
            </text>
            {complete && kind !== 'roof' && <CircleCheck aria-hidden x={x + w - 64} y={y + hgt / 2 - 8} width={15} height={15} color={ink} />}
            {s.overdue > 0 && mode !== 'schedule' && (
              <g aria-hidden>
                <circle cx={x + w - 62} cy={y + hgt / 2 - 1} r={8.5} fill="var(--bad)" />
                <text x={x + w - 62} y={y + hgt / 2 + 3} fontSize={10} fontWeight={700} textAnchor="middle" fill="#fff" className="num select-none">
                  {s.overdue > 99 ? '99' : s.overdue}
                </text>
              </g>
            )}
            {mode === 'schedule' && s.tasks > 0 && <Icon x={x + w - 74} y={y + hgt / 2 - 8} width={16} height={16} color={SCHEDULE_INK[s.schedule]} aria-hidden />}
          </>
        )}
      </g>
    );
  };

  let x = g.pad;
  return (
    <div className="scroll-x">
      <svg
        viewBox={`0 0 ${width + g.dx} ${totalH}`}
        width={small ? width + g.dx : '100%'}
        height={small ? totalH : undefined}
        style={small ? undefined : { maxWidth: maxWidth ?? width + g.dx, minWidth: Math.min(width, 520) }}
        role="group"
        aria-label={t('analysis.buildingAria')}
        className="block overflow-visible"
      >
        <defs>
          <pattern id="pz-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill="var(--sheet)" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="var(--hatch)" strokeWidth="1.5" />
          </pattern>
          <pattern id="pz-soil" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="8" stroke="var(--line)" strokeWidth="1" />
          </pattern>
        </defs>
        {/* ground plane in the same oblique view, soil below for basements */}
        <polygon points={`0,${ground} ${g.dx},${ground - g.dy} ${width + g.dx},${ground - g.dy} ${width},${ground}`} fill="var(--sheet-2)" stroke="var(--line)" strokeWidth={1} aria-hidden />
        {maxBelow > 0 && <rect x={0} y={ground} width={width} height={maxBelow + 4} fill="url(#pz-soil)" aria-hidden />}
        <line x1={0} x2={width} y1={ground} y2={ground} stroke="var(--graphite)" strokeWidth={small ? 1 : 1.5} aria-hidden />
        {!small && (
          <text x={4} y={ground + 12} fontSize={9.5} fill="var(--graphite)" className="num select-none" aria-hidden>
            ±0
          </text>
        )}
        {buildings.map((b, bi) => {
          const bx = x;
          x += step;
          const ups = b.floors.filter((f) => f.rank >= 0);
          const downs = b.floors.filter((f) => f.rank < 0).reverse();
          let y = ground;
          const items: ReactNode[] = [];
          ups.forEach((f, i) => {
            const hgt = height(f.rank);
            y -= hgt;
            const isSel = !!selected && selected.building === b.key && selected.floor === f.key;
            const top = i === ups.length - 1;
            items.push(
              <Fragment key={`u${f.key}`}>
                {floorBox(f, bx, y, g.bw, hgt, f.label, `${b.label ? `${b.label} ` : ''}${f.label}`, onSelect ? () => onSelect(b.key, f.key) : undefined, isSel, f.rank >= 1000 ? 'roof' : f.rank === 0 ? 'ground' : 'floor', top)}
              </Fragment>,
            );
          });
          const topY = y - g.dy;
          let dy = ground + (small ? 1 : 2);
          for (const f of downs) {
            const isSel = !!selected && selected.building === b.key && selected.floor === f.key;
            items.push(
              <Fragment key={`d${f.key}`}>
                {floorBox(f, bx, dy, g.bw, g.fh, f.label, `${b.label ? `${b.label} ` : ''}${f.label}`, onSelect ? () => onSelect(b.key, f.key) : undefined, isSel, 'floor', false)}
              </Fragment>,
            );
            dy += g.fh;
          }
          const total = b.total;
          return (
            <g key={b.key || bi}>
              {!small && (
                <g
                  role={onSelect ? 'button' : undefined}
                  tabIndex={onSelect ? 0 : undefined}
                  className={onSelect ? 'cursor-pointer outline-none' : undefined}
                  onClick={onSelect ? () => onSelect(b.key, null) : undefined}
                  onKeyDown={onSelect ? keyHandler(() => onSelect(b.key, null)) : undefined}
                  aria-label={describe(b.label || t('analysis.wholeProject'), total)}
                >
                  <text x={bx} y={topY - 22} fontSize={13} fontWeight={600} fill="var(--ink)" className="select-none">
                    {b.label || t('analysis.wholeProject')}
                  </text>
                  <text x={bx} y={topY - 8} fontSize={11} fill="var(--graphite)" className="num select-none">
                    {t('analysis.buildingTotal', { progress: pctOr(progressOf(total, progressBy)), done: num(total.done), tasks: num(total.tasks) })}
                  </text>
                </g>
              )}
              {items}
              {!small && b.general && (
                <g transform={`translate(0, ${(downs.length ? dy : ground + 2) + 8})`}>
                  {floorBox(
                    b.general,
                    bx + 24,
                    0,
                    g.bw - 48,
                    34,
                    t('analysis.general'),
                    `${b.label ? `${b.label}: ` : ''}${t('analysis.general')}`,
                    onSelect ? () => onSelect(b.key, null) : undefined,
                    false,
                    'area',
                    false,
                  )}
                </g>
              )}
            </g>
          );
        })}
        {areas.map((a, ai) => {
          const ax = g.pad + buildings.length * step + ai * (areaW + g.dx + 16);
          return (
            <g key={a.key}>
              <text x={ax} y={ground - g.fh - g.dy - 8} fontSize={11} fill="var(--graphite)" className="select-none">
                {t('analysis.area')}
              </text>
              {floorBox(a, ax, ground - g.fh, areaW, g.fh, a.label || t('analysis.none'), a.label || t('analysis.none'), onSelect ? () => onSelect(a.key, null) : undefined, !!selected && selected.building === a.key, 'area', true)}
            </g>
          );
        })}
      </svg>
      {tip.node}
    </div>
  );
}

export interface MiniFloor {
  label: string;
  rank: number;
  tasks: number;
  done: number;
  progress: number;
  schedule: ScheduleState;
}

/** Small picture of the buildings for the portfolio cards: storeys coloured by progress in the same oblique view. */
export function MiniBuilding({ buildings, label }: { buildings: { label: string; floors: MiniFloor[] }[]; label: string }) {
  if (!buildings.length) return null;
  const bw = 46;
  const fh = 8;
  const gap = 14;
  const dx = 5;
  const dy = 3;
  const above = buildings.map((b) => b.floors.filter((f) => f.rank >= 0).length);
  const below = buildings.map((b) => b.floors.filter((f) => f.rank < 0).length);
  const ground = 4 + dy + Math.max(...above) * fh;
  const width = 4 + buildings.length * (bw + dx + gap);
  const height = ground + Math.max(0, ...below) * fh + 4;
  const side = (x: number, y: number, fill: string) => (
    <>
      <polygon points={`${x + bw},${y} ${x + bw + dx},${y - dy} ${x + bw + dx},${y + fh - 1 - dy} ${x + bw},${y + fh - 1}`} fill={fill} stroke="var(--line-strong)" strokeWidth={0.5} />
      <polygon points={`${x + bw},${y} ${x + bw + dx},${y - dy} ${x + bw + dx},${y + fh - 1 - dy} ${x + bw},${y + fh - 1}`} fill="#000" fillOpacity={0.2} />
    </>
  );
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={Math.min(width, 260)} height={(Math.min(width, 260) / width) * height} role="img" aria-label={label} className="block">
      <polygon points={`0,${ground + 0.5} ${dx},${ground + 0.5 - dy} ${width},${ground + 0.5 - dy} ${width - dx},${ground + 0.5}`} fill="var(--sheet-2)" aria-hidden />
      {buildings.map((b, bi) => {
        const x = 2 + bi * (bw + dx + gap);
        const ups = b.floors.filter((f) => f.rank >= 0);
        const downs = b.floors.filter((f) => f.rank < 0).reverse();
        return (
          <g key={bi}>
            <title>{b.label}</title>
            {ups.map((f, i) => {
              const y = ground - (i + 1) * fh;
              const fill = f.tasks ? progFill(f.progress) : 'var(--sheet-2)';
              const top = i === ups.length - 1;
              return f.rank >= 1000 ? (
                <g key={f.label}>
                  <polygon points={`${x + 2},${y + fh - 1} ${x + bw - 2},${y + fh - 1} ${x + bw - 8},${y + 1} ${x + 8},${y + 1}`} fill={fill} stroke="var(--line-strong)" strokeWidth={0.75}>
                    <title>{`${b.label} ${f.label}: ${f.done}/${f.tasks}`}</title>
                  </polygon>
                </g>
              ) : (
                <g key={f.label}>
                  {side(x, y, fill)}
                  {top && (
                    <>
                      <polygon points={`${x},${y} ${x + dx},${y - dy} ${x + bw + dx},${y - dy} ${x + bw},${y}`} fill={fill} stroke="var(--line-strong)" strokeWidth={0.5} />
                      <polygon points={`${x},${y} ${x + dx},${y - dy} ${x + bw + dx},${y - dy} ${x + bw},${y}`} fill="#fff" fillOpacity={0.3} />
                    </>
                  )}
                  <rect x={x} y={y} width={bw} height={fh - 1} rx={0.5} fill={fill} stroke="var(--line-strong)" strokeWidth={0.75}>
                    <title>{`${b.label} ${f.label}: ${f.done}/${f.tasks}`}</title>
                  </rect>
                </g>
              );
            })}
            {downs.map((f, i) => (
              <g key={f.label}>
                {side(x, ground + 1 + i * fh, f.tasks ? progFill(f.progress) : 'var(--sheet-2)')}
                <rect x={x} y={ground + 1 + i * fh} width={bw} height={fh - 1} rx={0.5} fill={f.tasks ? progFill(f.progress) : 'var(--sheet-2)'} stroke="var(--line-strong)" strokeWidth={0.75}>
                  <title>{`${b.label} ${f.label}: ${f.done}/${f.tasks}`}</title>
                </rect>
              </g>
            ))}
          </g>
        );
      })}
      <line x1={0} x2={width} y1={ground + 0.5} y2={ground + 0.5} stroke="var(--graphite)" strokeWidth={1} />
    </svg>
  );
}
